import React from "react";
import { Handle, Position } from "reactflow";
import {
  Box,
  Text, Heading,
  Badge,
  VStack,
  HStack,
  Avatar,
  useTheme,
  Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverArrow, PopoverCloseButton,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
//import { ReactComponent as TotalIcon } from '../assets/icons/syringe.svg'; // หรือ syringe.svg
import { ReactComponent as VaccineIcon } from "../assets/icons/syringe.svg"; // หรือ syringe.svg
import { ReactComponent as RegionIcon } from "../assets/icons/region.svg";
import { ReactComponent as ProvinceIcon } from "../assets/icons/province.svg";
import { ReactComponent as CowIcon } from "../assets/icons/cow.svg";
import { ReactComponent as PigIcon } from "../assets/icons/pig.svg";
import { ReactComponent as ChickenIcon } from "../assets/icons/chicken.svg";
import { ReactComponent as OtherIcon } from "../assets/icons/other.svg"; // หรือ paw.svg

import {
  FaSyringe,
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaHorse,
  FaPiggyBank,
  FaFeatherAlt,
  FaPaw,
} from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faFileSignature,
  faScrewdriverWrench,
} from "@fortawesome/free-solid-svg-icons";

// --- ส่วนที่ 1: ตั้งค่าสีและไอคอน ---

// 1a. สีสำหรับ 'สายพันธุ์ข้อมูล' (ใช้กับประเภทสัตว์ และวัคซีน)
const categoryColors = {
  CATTLE: "blue",
  PIG: "pink",
  POULTRY: "orange",
  OTHERS: "gray",
};

// 1b. สีสำหรับ 'ระดับชั้นข้อมูล' (ใช้กับเขต, จังหวัด, และทั้งหมด)
const typeColors = {
  เขต: "green",
  จังหวัด: "purple",
  ทั้งหมด: "teal",
};

// 1c. ไอคอนสำหรับ Node แต่ละประเภท
const typeIcons = {
  ทั้งหมด: VaccineIcon,
  วัคซีน: VaccineIcon,
  เขต: RegionIcon,
  จังหวัด: ProvinceIcon,
  ประเภทสัตว์: {
    CATTLE: CowIcon,
    PIG: PigIcon,
    POULTRY: ChickenIcon,
    OTHERS: OtherIcon,
  },
};

const MotionBox = motion(Box);

// --- ส่วนที่ 2: Component หลัก ---
function CustomNode({ data }) {
  const theme = useTheme();

  if (data.isInvisible) {
    return null; // สำหรับ Dummy Node
  }

  // --- ส่วนที่ 3: Logic การเลือกสีและไอคอน (ปรับปรุงใหม่) ---
  let colorScheme;
  let IconComponent;

  // กำหนดสีตามเงื่อนไข
  if (data.type === "ประเภทสัตว์" || data.type === "วัคซีน") {
    // ถ้าเป็นชั้น ประเภทสัตว์ หรือ วัคซีน -> ใช้สีตาม Category
    colorScheme = categoryColors[data.livestockCategory] || "gray";
  } else {
    // ถ้าเป็นชั้นอื่น (เขต, จังหวัด, ทั้งหมด) -> ใช้สีตาม Type
    colorScheme = typeColors[data.type] || "gray";
  }

  // กำหนดไอคอน (Logic เดิมยังใช้ได้ดี)
  if (data.type === "ประเภทสัตว์") {
    IconComponent = typeIcons.ประเภทสัตว์[data.livestockCategory] || FaPaw;
  } else {
    IconComponent = typeIcons[data.type];
  }

  // --- สร้างค่า boxShadow แบบไดนามิก ---
  let dynamicBoxShadow = "md"; // ค่าเริ่มต้น
  if (data.isHighlighted) {
    // ดึงค่าสีจริงๆ จาก theme เช่น 'blue' -> '#3182CE'
    // เราใช้สีรหัส 400 เพราะเป็นสีที่ไม่เข้มหรืออ่อนจนเกินไปสำหรับทำเงา
    const highlightColor = theme.colors[colorScheme]
      ? theme.colors[colorScheme][300]
      : "#404040";
    dynamicBoxShadow = `0 0 12px 4px ${highlightColor}`;
  }

const NodeContent = (
      <MotionBox
        // --- 3. เพิ่ม Props สำหรับควบคุม Animation ---
        initial={{ opacity: 0, scale: 0.5 }} // สถานะเริ่มต้น: โปร่งใส และมีขนาด 80%
        animate={{ opacity: 1, scale: 1 }} // สถานะสิ้นสุด: ทึบ และมีขนาด 100%
        transition={{ duration: 0.5, ease: "easeOut" }} // กำหนดความเร็วและลักษณะการเคลื่อนไหว
        // --- Props เดิมของ Box ยังคงอยู่เหมือนเดิม ---
        borderWidth={data.isHighlighted ? "3px" : "2px"}
        borderRadius="lg"
        boxShadow={dynamicBoxShadow}
        bg="white"
        borderColor={`${colorScheme}.300`}
        width="280px"
      >
        <VStack align="stretch">
          <HStack
            bg={`${colorScheme}.500`}
            color="white"
            p={1}
            borderTopRadius="md"
          >
            {IconComponent && (
              <IconComponent 
                fill="white" 
                width="20px" 
                height="20px" 
                style={{ marginRight: '8px' }} 
              />
            )}
            <Text fontSize="xl" fontWeight="normal" noOfLines={3}>{data.label}</Text>
          </HStack>
          <VStack align="start" ml={2}>
            <Text fontSize="xl" fontWeight="bold">
              {data.stock ? data.stock.toLocaleString() : 'N/A'}{" "}
              <span style={{ fontSize: "1rem" }}>โดส</span>
            </Text>
          </VStack>
        </VStack>
      </MotionBox>
  );

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ background: "#555" }} />

      {/* 2. ครอบ Node ของเราด้วย Popover */}
      <Popover trigger="hover" placement="right" openDelay={200}>
        <PopoverTrigger>
            {/* วาง Node ของเราที่สร้างไว้ข้างบน */}
            {NodeContent}
        </PopoverTrigger>
        
        {/* 3. สร้างเนื้อหาที่จะแสดงใน Tooltip */}
        <PopoverContent boxShadow="xl" width="200px">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody>
            <VStack align="stretch" spacing={2} py={2}>
              <HStack justify="space-between">
                <Heading fontSize="3xl" color="gray.500">{data.label}</Heading>
              </HStack>
              <HStack justify="space-between">
                <Heading fontSize="3xl" fontWeight="bold" color={`${colorScheme}.600`}>
                  {data.stock ? data.stock.toLocaleString() : 'N/A'} โดส
                </Heading>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      {!data.isTerminal && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: "#555" }}
        />
      )}
    </>
  );
}

export default CustomNode;
